import Drawer from '@/components/Drawer'
import AsyncSelect from '@/components/Form/Shared/AsyncSelect'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import CloseIcons from '@/icons/CloseIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import FilterAltIcons from '@/icons/FilterAltIcons'
import React, { ReactNode, useMemo, useState, memo, useCallback } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, FormItem } from 'vnetwork-ui'

// Filter field configuration - reusable for any table
export interface FilterFieldConfig {
    name: string
    type:
        | 'select'
        | 'async-select'
        | 'employee-select'
        | 'boolean'
        | 'string'
        | 'number'
    label: string
    placeholder?: string
    options?: { label: string; value: any }[]
    asyncOptions?: () => Promise<{ label: string; value: any }[]>
    multiple?: boolean
    isQuickFilter?: boolean // Always show in quick filter bar
    defaultValue?: any // Default value for the field
    hidden?: boolean // Hide field from UI but still include in filters
}

interface FilterBarProps {
    control: any
    watch?: (name: string) => any
    setValue?: UseFormSetValue<any>
    filterFields: FilterFieldConfig[]
    drawerTitle?: string
    extraActions?: ReactNode[]
}

const FilterBar: React.FC<FilterBarProps> = memo(
    ({
        control,
        watch = () => {},
        setValue = () => {},
        filterFields,
        drawerTitle,
        extraActions = [],
    }) => {
        const { t } = useTranslation()
        const [isDrawerOpen, setIsDrawerOpen] = useState(false)

        // Separate quick filters and more filters (exclude hidden fields)
        const quickFilters = useMemo(
            () => filterFields.filter((f) => f.isQuickFilter && !f.hidden),
            [filterFields]
        )
        const moreFilters = useMemo(
            () => filterFields.filter((f) => !f.isQuickFilter && !f.hidden),
            [filterFields]
        )

        // Check if a filter has an active value
        const hasActiveValue = useCallback(
            (field: FilterFieldConfig, value: any): boolean => {
                if (field.type === 'boolean') {
                    return value === true || value === false
                }
                if (value === undefined || value === null) return false
                if (Array.isArray(value) && value.length === 0) return false
                if (value === '') return false
                return true
            },
            []
        )

        // Get more filters that have values (to show outside)
        // Note: We need to call watch for each field to subscribe to changes
        const moreFilterValues = moreFilters.map((field) => ({
            field,
            value: watch(field.name),
        }))

        const activeMoreFilters = useMemo(() => {
            return moreFilterValues
                .filter(({ field, value }) => hasActiveValue(field, value))
                .map(({ field }) => field)
        }, [moreFilterValues, hasActiveValue])

        // More filters that don't have values (remain in drawer)
        const inactiveMoreFilters = useMemo(() => {
            return moreFilterValues
                .filter(({ field, value }) => !hasActiveValue(field, value))
                .map(({ field }) => field)
        }, [moreFilterValues, hasActiveValue])

        // Count inactive more filters (for badge)
        const inactiveMoreFiltersCount = inactiveMoreFilters.length

        const getDefaultValue = useCallback((field: FilterFieldConfig) => {
            if (field.defaultValue !== undefined) return field.defaultValue
            switch (field.type) {
                case 'boolean':
                    return undefined
                case 'number':
                    return undefined
                case 'string':
                    return ''
                default:
                    return field.multiple ? [] : ''
            }
        }, [])

        const handleClearFilter = useCallback(
            (field: FilterFieldConfig) => {
                setValue(field.name, getDefaultValue(field))
            },
            [setValue, getDefaultValue]
        )

        const handleClearAllMoreFilters = useCallback(() => {
            moreFilters.forEach((field) => {
                setValue(field.name, getDefaultValue(field))
            })
        }, [moreFilters, setValue, getDefaultValue])

        // Render a filter field based on its config
        const renderFilterField = (
            field: FilterFieldConfig,
            showClearButton: boolean = false
        ) => {
            const fieldElement = (() => {
                switch (field.type) {
                    case 'async-select':
                        return (
                            <AsyncSelect
                                asyncOptions={field.asyncOptions!}
                                // @ts-ignore - generic type inference issue
                                name={field.name}
                                control={control}
                                label={field.label}
                                placeholder={field.placeholder || field.label}
                                multiple={field.multiple}
                                size="small"
                            />
                        )
                    case 'employee-select':
                        return (
                            <div className="max-w-full">
                                <EmployeeSelect
                                    control={control}
                                    name={field.name}
                                    multiple={field.multiple}
                                />
                            </div>
                        )
                    case 'boolean':
                        return (
                            <FormItem
                                name={field.name}
                                control={control}
                                type="checkbox"
                                label={field.label}
                                size="small"
                            />
                        )
                    case 'string':
                        return (
                            <FormItem
                                name={field.name}
                                control={control}
                                type="textfield"
                                label={field.label}
                                placeholder={field.placeholder || field.label}
                                size="small"
                            />
                        )
                    case 'number':
                        return (
                            <FormItem
                                name={field.name}
                                control={control}
                                type="number"
                                label={field.label}
                                placeholder={field.placeholder || field.label}
                                size="small"
                            />
                        )
                    default:
                        return (
                            <FormItem
                                name={field.name}
                                control={control}
                                type="select"
                                label={field.label}
                                placeholder={field.placeholder || field.label}
                                options={field.options || []}
                                multiple={field.multiple}
                                size="small"
                            />
                        )
                }
            })()

            if (showClearButton) {
                return (
                    <div className="relative group">
                        {fieldElement}
                        <button
                            type="button"
                            onClick={() => handleClearFilter(field)}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-gray-400 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('clear-filter')}
                        >
                            <CloseIcons className="w-2.5 h-2.5" />
                        </button>
                    </div>
                )
            }

            return fieldElement
        }

        return (
            <>
                <div className="mb-2 w-full">
                    <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                        {/* Quick Filters - Always visible */}
                        {quickFilters?.map((field) => (
                            <div
                                key={field.name}
                                className="col-span-1 max-w-full"
                            >
                                {renderFilterField(field)}
                            </div>
                        ))}

                        {/* Active More Filters - Show outside when have value */}
                        {activeMoreFilters?.map((field) => (
                            <div key={field.name} className="col-span-1">
                                {renderFilterField(field, true)}
                            </div>
                        ))}
                        {/* Extra Actions */}
                        {extraActions &&
                            extraActions.length > 0 &&
                            extraActions.map((action, index) => (
                                <div key={index} className="col-span-1">
                                    {action}
                                </div>
                            ))}
                        {/* More Filters Button */}
                        {(inactiveMoreFiltersCount > 0 ||
                            activeMoreFilters.length > 0) && (
                            <div className="col-span-1 flex items-center gap-2">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<FilterAltIcons />}
                                    endIcon={
                                        activeMoreFilters.length > 0 ? (
                                            <DeleteOutlineIcons
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleClearAllMoreFilters()
                                                }}
                                                className="w-3.5 h-3.5 text-gray-500 hover:text-red-500"
                                            />
                                        ) : undefined
                                    }
                                    onClick={() => setIsDrawerOpen(true)}
                                    className="whitespace-nowrap w-full"
                                >
                                    {t('more-filters')}
                                    {inactiveMoreFiltersCount > 0 && (
                                        <span className="ml-1 bg-gray-200 text-gray-600 text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                            {inactiveMoreFiltersCount}
                                        </span>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* More Filters Drawer */}
                <Drawer
                    open={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    width="400px"
                >
                    <div className="flex flex-col h-full">
                        {/* Drawer Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="font-semibold text-gray-800">
                                {drawerTitle || t('more-filters')}
                            </h3>
                            <button
                                type="button"
                                onClick={() => setIsDrawerOpen(false)}
                                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                            >
                                <CloseIcons className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        {/* Drawer Content - Only show inactive filters */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4">
                            {inactiveMoreFilters.length > 0 ? (
                                inactiveMoreFilters.map((field) => (
                                    <div key={field.name}>
                                        <label className="text-sm text-gray-700 mb-1.5 block font-medium">
                                            {field.label}
                                        </label>
                                        {renderFilterField(field)}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center text-gray-500 py-8">
                                    {t('all-filters-active')}
                                </div>
                            )}
                        </div>
                    </div>
                </Drawer>
            </>
        )
    }
)

FilterBar.displayName = 'FilterBar'

export default FilterBar
